import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import {
  adminOnboardSociety,
  OnboardRequest,
  OnboardFlat,
  OnboardResult,
} from '../services/api';
import { downloadOnboardTemplate } from '../utils/onboardExcel';
import './AdminSocietyOnboard.css';

const VALID_SOCIETY_ROLES = ['president', 'treasurer', 'body_member', 'normal_member', 'guard'];

// ─── Parse uploaded file ─────────────────────────────────────────────────────
function parseSheet<T extends Record<string, string>>(ws: XLSX.WorkSheet): T[] {
  return XLSX.utils.sheet_to_json<T>(ws, { defval: '', raw: false });
}

/** Safely coerce any cell value (may be number, boolean, null) to trimmed string */
function str(v: unknown): string {
  if (v === null || v === undefined) return '';
  return String(v).trim();
}

type ParsedBlock = { blockName: string; numberOfFloors: number; customLetter: string; _rowIndex: number; _error?: string };
type ParsedRow = OnboardFlat & { _rowIndex: number; _error?: string; _vehicleCount: number };

// ─── Component ───────────────────────────────────────────────────────────────
const AdminSocietyOnboard: React.FC = () => {
  const fileRef = useRef<HTMLInputElement>(null);

  const [fileName, setFileName] = useState('');
  const [society, setSociety] = useState<OnboardRequest['society'] | null>(null);
  const [blockRows, setBlockRows] = useState<ParsedBlock[]>([]);
  const [flatRows, setFlatRows] = useState<ParsedRow[]>([]);
  const [parseError, setParseError] = useState('');

  const [previewPage, setPreviewPage] = useState(0);
  const PAGE_SIZE = 20;

  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<OnboardResult | null>(null);
  const [importError, setImportError] = useState('');

  const reset = () => {
    setFileName('');
    setSociety(null);
    setBlockRows([]);
    setFlatRows([]);
    setParseError('');
    setResult(null);
    setImportError('');
    setPreviewPage(0);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setParseError('');
    setSociety(null);
    setBlockRows([]);
    setFlatRows([]);
    setResult(null);
    setImportError('');

    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });

      // ── 1. Society sheet ──────────────────────────────────────────────────
      // Numbers adds an "Export Summary" sheet — skip it when looking for sheets
      const ignoredSheets = new Set(['Export Summary', 'Instructions']);
      const socSheet = wb.Sheets['Society'] ?? wb.Sheets[wb.SheetNames.find(n => !ignoredSheets.has(n)) || ''];
      if (!socSheet) throw new Error('No "Society" sheet found in the workbook.');

      type SocRow = Record<string, string>;
      const socRows = parseSheet<SocRow>(socSheet);
      const sr = socRows[0];
      if (!sr) throw new Error('"Society" sheet has no data rows.');

      const g = (keys: string[]) => keys.map(k => str(sr[k])).find(v => v) || '';

      const soc: OnboardRequest['society'] = {
        name:           g(['society_name', 'Society Name', 'name']),
        location:       g(['location', 'Location', 'address']),
        city:           g(['city', 'City']),
        state:          g(['state', 'State']),
        pincode:        g(['pincode', 'Pincode']),
        country:        g(['country', 'Country']) || 'India',
        type:           g(['type', 'Type']) || 'multiStory',
        flatNoFormat:   g(['flat_no_format', 'flatNoFormat', 'Flat No Format']) || undefined,
        latlong:        g(['latlong', 'LatLong', 'lat_long']) || undefined,
        account_number: g(['account_number', 'Account Number']) || undefined,
        account_ifsc:   g(['account_ifsc', 'IFSC', 'Account IFSC']) || undefined,
      };
      if (!soc.name) throw new Error('"society_name" is required in the Society sheet.');
      if (!soc.location) throw new Error('"location" is required in the Society sheet.');
      if (!soc.city) throw new Error('"city" is required in the Society sheet.');
      setSociety(soc);

      // ── 2. Blocks sheet ───────────────────────────────────────────────────
      const blkSheet = wb.Sheets['Blocks'] ?? wb.Sheets['Block'];
      if (!blkSheet) throw new Error('No "Blocks" sheet found. Please add a Blocks sheet (use the template).');

      type BlkRow = Record<string, string>;
      const rawBlocks = parseSheet<BlkRow>(blkSheet);
      const parsedBlocks: ParsedBlock[] = rawBlocks.map((r, idx) => {
        const blockName = str(r['block_name'] || r['Block Name'] || r['blockName']);
        const floorsRaw = str(r['number_of_floors'] || r['Number of Floors'] || r['numberOfFloors']);
        let error: string | undefined;
        if (!blockName) error = 'Missing block_name';
        else if (!floorsRaw || isNaN(Number(floorsRaw))) error = 'number_of_floors must be a number';
        return {
          _rowIndex: idx + 2,
          _error: error,
          blockName,
          numberOfFloors: floorsRaw ? Number(floorsRaw) : 1,
          customLetter: str(r['custom_letter'] || r['Custom Letter'] || r['customLetter']),
        };
      });
      if (parsedBlocks.length === 0) throw new Error('"Blocks" sheet has no data rows.');
      setBlockRows(parsedBlocks);

      // Build set of valid block names for flat validation
      const validBlockNames = new Set(parsedBlocks.filter(b => !b._error).map(b => b.blockName.toLowerCase()));

      // ── 3. Flats sheet ────────────────────────────────────────────────────
      const flatSheet = wb.Sheets['Flats'] ?? wb.Sheets[wb.SheetNames.find(n => !ignoredSheets.has(n) && n !== 'Society' && n !== 'Blocks') || ''];
      if (!flatSheet) throw new Error('No "Flats" sheet found in the workbook.');

      type FlatRow = Record<string, string>;
      const rawFlats = parseSheet<FlatRow>(flatSheet);
      const parsed: ParsedRow[] = rawFlats.map((r, idx) => {
        const flatNumber = str(r['flat_number'] || r['Flat Number'] || r['flat']);
        const block = str(r['block'] || r['Block']);
        let error: string | undefined;
        if (!flatNumber) error = 'Missing flat_number';
        else if (!block) error = 'Missing block';
        else if (!validBlockNames.has(block.toLowerCase())) error = `Block "${block}" not in Blocks sheet`;

        const twoW = str(r['two_wheelers'] || r['Two Wheelers'] || r['2wheeler']);
        const fourW = str(r['four_wheelers'] || r['Four Wheelers'] || r['Car'] || r['4wheeler']);
        const vehicleCount = [
          ...twoW.split(',').map(v => v.trim()).filter(Boolean),
          ...fourW.split(',').map(v => v.trim()).filter(Boolean),
        ].length;

        const rawSocietyRole = str(r['society_role'] || r['Society Role']).toLowerCase();
        const societyRole = rawSocietyRole && VALID_SOCIETY_ROLES.includes(rawSocietyRole) ? rawSocietyRole : '';
        if (rawSocietyRole && !societyRole && !error) {
          error = `Unknown society_role "${rawSocietyRole}"`;
        }

        const floorVal = str(r['floor']);
        const areaVal = str(r['area_sqft'] || r['area']);
        const flatTypeVal = str(r['flat_type'] || r['Flat Type'] || r['flatType']);
        const roleRaw = str(r['role_in_flat']).toLowerCase();

        return {
          _rowIndex: idx + 2,
          _error: error,
          _vehicleCount: vehicleCount,
          block,
          flat_number: flatNumber,
          floor: floorVal ? Number(floorVal) : undefined,
          area: areaVal ? Number(areaVal) : undefined,
          flat_type: flatTypeVal || undefined,
          user_name: str(r['user_name'] || r['User Name']),
          user_phone: str(r['user_phone'] || r['Phone']),
          user_email: str(r['user_email'] || r['Email']),
          role_in_flat: roleRaw === 'tenant' ? 'tenant' : 'owner',
          society_role: societyRole,
          two_wheelers: twoW,
          four_wheelers: fourW,
        };
      });

      setFlatRows(parsed);
      setPreviewPage(0);
    } catch (err: any) {
      setParseError(err.message || 'Failed to parse file');
    }
  };

  const errorCount = flatRows.filter(r => r._error).length;
  const blockErrorCount = blockRows.filter(b => b._error).length;
  const validRows = flatRows.filter(r => !r._error);
  const validBlocks = blockRows.filter(b => !b._error);

  const pageRows = flatRows.slice(previewPage * PAGE_SIZE, (previewPage + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(flatRows.length / PAGE_SIZE);

  const handleImport = async () => {
    if (!society || validRows.length === 0) return;
    setImporting(true);
    setImportError('');
    setResult(null);
    try {
      const payload: OnboardRequest = {
        society,
        blocks: validBlocks.map(b => ({ blockName: b.blockName, numberOfFloors: b.numberOfFloors, customLetter: b.customLetter || undefined })),
        flats: validRows.map(r => ({
          block: r.block,
          flat_number: r.flat_number,
          floor: r.floor,
          area: r.area,
          flat_type: r.flat_type,
          user_name: r.user_name,
          user_phone: r.user_phone,
          user_email: r.user_email,
          role_in_flat: r.role_in_flat,
          society_role: r.society_role,
          two_wheelers: r.two_wheelers,
          four_wheelers: r.four_wheelers,
        })),
      };
      const res = await adminOnboardSociety(payload);
      setResult(res.data);
    } catch (err: any) {
      setImportError(err.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="aob-page">
      <div className="aob-header">
        <div>
          <h2 className="aob-title">Onboard New Society</h2>
          <p className="aob-subtitle">
            Download the template, fill in society details, blocks, flats and (optionally) resident
            info, then upload it here to create everything in one go.
          </p>
        </div>
        <button className="aob-btn aob-btn-primary" onClick={downloadOnboardTemplate}>
          ⬇ Download Template (.xlsx)
        </button>
      </div>

      {/* Upload zone */}
      <div className="aob-card">
        <div className="aob-card-title">Upload Filled Excel</div>
        <div className="aob-upload-zone" onClick={() => fileRef.current?.click()}>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: 'none' }}
            onChange={handleFile}
          />
          <div className="aob-upload-icon">📂</div>
          {fileName ? (
            <div className="aob-upload-file">{fileName}</div>
          ) : (
            <div className="aob-upload-hint">Click to choose an Excel file (.xlsx)</div>
          )}
        </div>
        {parseError && <div className="aob-alert aob-alert-error">⚠ {parseError}</div>}
      </div>

      {/* Society + Blocks summary */}
      {society && (
        <div className="aob-card">
          <div className="aob-card-title">Society to Create</div>
          <div className="aob-meta-grid">
            <div><span className="aob-label">Name</span> <strong>{society.name}</strong></div>
            <div><span className="aob-label">Location</span> {society.location}</div>
            <div><span className="aob-label">City</span> {society.city}</div>
            <div><span className="aob-label">State</span> {society.state}</div>
            <div><span className="aob-label">Pincode</span> {society.pincode}</div>
            <div><span className="aob-label">Country</span> {society.country || 'India'}</div>
            <div><span className="aob-label">Type</span> {society.type || 'multiStory'}</div>
            {society.flatNoFormat && <div><span className="aob-label">Flat No. Format</span> {society.flatNoFormat}</div>}
            {society.latlong && <div><span className="aob-label">Lat/Long</span> <span className="aob-cell-mono">{society.latlong}</span></div>}
            {society.account_number && <div><span className="aob-label">Account No.</span> {society.account_number}</div>}
            {society.account_ifsc && <div><span className="aob-label">IFSC</span> {society.account_ifsc}</div>}
          </div>

          {/* Blocks preview */}
          {blockRows.length > 0 && (
            <div className="aob-blocks-section">
              <div className="aob-label" style={{ marginBottom: 8 }}>
                Blocks ({validBlocks.length} valid{blockErrorCount > 0 ? `, ${blockErrorCount} with errors` : ''})
              </div>
              <div className="aob-blocks-table-wrap">
                <table className="aob-blocks-table">
                  <thead>
                    <tr><th>#</th><th>Block Name</th><th>Floors</th><th>Custom Letter</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {blockRows.map(b => (
                      <tr key={b._rowIndex} className={b._error ? 'aob-row-error' : ''}>
                        <td className="aob-cell-muted">{b._rowIndex}</td>
                        <td><strong>{b.blockName || <span className="aob-missing">MISSING</span>}</strong></td>
                        <td>{b.numberOfFloors}</td>
                        <td>{b.customLetter || '—'}</td>
                        <td>
                          {b._error
                            ? <span className="aob-badge aob-badge-error">{b._error}</span>
                            : <span className="aob-badge aob-badge-ok">✓</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="aob-stats-row" style={{ marginTop: 16 }}>
            <span className="aob-stat aob-stat-total">{flatRows.length} flat rows</span>
            <span className="aob-stat aob-stat-valid">{validRows.length} valid</span>
            {errorCount > 0 && <span className="aob-stat aob-stat-error">{errorCount} with errors</span>}
            <span className="aob-stat">
              {validRows.filter(r => r.user_name || r.user_phone || r.user_email).length} with user data
            </span>
            <span className="aob-stat">
              {validRows.filter(r => !r.user_name && !r.user_phone && !r.user_email).length} vacant
            </span>
            <span className="aob-stat">
              🚗 {validRows.reduce((sum, r) => sum + (r._vehicleCount ?? 0), 0)} vehicles
            </span>
          </div>
        </div>
      )}

      {/* Flat preview table */}
      {flatRows.length > 0 && (
        <div className="aob-card aob-card-full">
          <div className="aob-card-title-row">
            <div className="aob-card-title">Preview — Flats ({flatRows.length} rows)</div>
            <div className="aob-pagination">
              <button disabled={previewPage === 0} onClick={() => setPreviewPage(p => p - 1)} className="aob-pg-btn">‹</button>
              <span>Page {previewPage + 1} / {totalPages}</span>
              <button disabled={previewPage >= totalPages - 1} onClick={() => setPreviewPage(p => p + 1)} className="aob-pg-btn">›</button>
            </div>
          </div>
          <div className="aob-table-wrap">
            <table className="aob-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Block</th>
                  <th>Flat No.</th>
                  <th>Floor</th>
                  <th>Area</th>
                  <th>User Name</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Flat Role</th>
                  <th>Society Role</th>
                  <th>2-Wheelers</th>
                  <th>Cars (4W)</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map(row => (
                  <tr key={row._rowIndex} className={row._error ? 'aob-row-error' : ''}>
                    <td className="aob-cell-muted">{row._rowIndex}</td>
                    <td>{row.block || <span className="aob-missing">—</span>}</td>
                    <td><strong>{row.flat_number || <span className="aob-missing">MISSING</span>}</strong></td>
                    <td>{row.floor ?? '—'}</td>
                    <td>{row.area ? `${row.area} sqft` : '—'}</td>
                    <td>{row.user_name || <span className="aob-vacant">vacant</span>}</td>
                    <td className="aob-cell-mono">{row.user_phone || '—'}</td>
                    <td className="aob-cell-mono">{row.user_email || '—'}</td>
                    <td>{row.role_in_flat}</td>
                    <td>
                      {row.society_role
                        ? <span className="aob-badge aob-badge-role">{row.society_role}</span>
                        : <span className="aob-cell-muted">—</span>}
                    </td>
                    <td className="aob-cell-vehicles">{row.two_wheelers || <span className="aob-cell-muted">—</span>}</td>
                    <td className="aob-cell-vehicles">{row.four_wheelers || <span className="aob-cell-muted">—</span>}</td>
                    <td>
                      {row._error
                        ? <span className="aob-badge aob-badge-error">{row._error}</span>
                        : <span className="aob-badge aob-badge-ok">✓</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Import action */}
      {society && validBlocks.length > 0 && validRows.length > 0 && !result && (
        <div className="aob-import-bar">
          {importError && <div className="aob-alert aob-alert-error">⚠ {importError}</div>}
          {errorCount > 0 && (
            <div className="aob-alert aob-alert-warn">
              ⚠ {errorCount} row(s) with errors will be skipped during import.
            </div>
          )}
          <div className="aob-import-actions">
            <button className="aob-btn aob-btn-ghost" onClick={reset} disabled={importing}>
              Clear & start over
            </button>
            <button className="aob-btn aob-btn-primary" onClick={handleImport} disabled={importing}>
              {importing ? 'Importing…' : `Import ${validRows.length} flats into "${society.name}"`}
            </button>
          </div>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="aob-card aob-result-card">
          <div className="aob-result-success">✅ Society onboarded successfully!</div>
          <div className="aob-result-grid">
            <div className="aob-result-item">
              <span className="aob-result-val">{result.societyId}</span>
              <span className="aob-result-label">Society ID</span>
            </div>
            <div className="aob-result-item">
              <span className="aob-result-val aob-passcode">{result.societyPasscode}</span>
              <span className="aob-result-label">Passcode</span>
            </div>
            <div className="aob-result-item">
              <span className="aob-result-val">{result.blocksCreated}</span>
              <span className="aob-result-label">Blocks created</span>
            </div>
            <div className="aob-result-item">
              <span className="aob-result-val">{result.flatsCreated}</span>
              <span className="aob-result-label">Flats created</span>
            </div>
            <div className="aob-result-item">
              <span className="aob-result-val">{result.usersCreated}</span>
              <span className="aob-result-label">Users created</span>
            </div>
            <div className="aob-result-item">
              <span className="aob-result-val">{result.usersLinked}</span>
              <span className="aob-result-label">Users linked</span>
            </div>
            <div className="aob-result-item">
              <span className="aob-result-val">{result.vehiclesCreated}</span>
              <span className="aob-result-label">Vehicles added</span>
            </div>
            <div className="aob-result-item">
              <span className="aob-result-val">{result.rolesAssigned}</span>
              <span className="aob-result-label">Roles assigned</span>
            </div>
          </div>
          {result.warnings.length > 0 && (
            <div className="aob-warnings">
              <strong>Warnings ({result.warnings.length})</strong>
              <ul>
                {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}
          <div className="aob-result-actions">
            <button className="aob-btn aob-btn-ghost" onClick={reset}>Onboard another society</button>
            <a className="aob-btn aob-btn-primary" href="/admin/societies">View all societies →</a>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSocietyOnboard;
